'use client'
// =============================================================================
// CommandPalette — Ctrl+K / Cmd+K (Slice 3, AppShell v0.8). Voir
// docs/architecture/command-center-ui.md, section Topbar : "même
// GlobalSearchService que la Sidebar — implémentation unique partagée entre
// les deux points d'entrée, jamais deux moteurs de recherche distincts."
//
// Trois sources, toutes réelles, aucune fabriquée :
//  - NAVIGATION : NAV_SECTIONS exporté par Sidebar.tsx (source unique, jamais
//    une 2e liste de destinations dupliquée) — filtrage local, instantané.
//  - MISSIONS   : /api/missions?limit=500 (même endpoint que MissionCard/
//    l'ancien Footer) — chargé une fois à l'ouverture, filtré localement.
//  - CONNAISSANCES : /api/search?q=…&sources=es,memory,legal (SearchService
//    réel, P2 Search AI) — requête live debounced, jamais un mock.
// =============================================================================
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { NAV_SECTIONS, type NavItemType } from './Sidebar'
import { useModulePrefs } from '../../stores/module-prefs.store'
import { isHrefVisible } from '@/lib/module-catalog'

interface MissionLite {
  id: string
  objective: string
  status: string
}

interface SearchHit {
  title?: string
  url?: string
  snippet?: string
  source?: string
}

type ResultKind = 'nav' | 'mission' | 'doc'

interface Result {
  kind: ResultKind
  key: string
  label: string
  detail?: string
  href: string
  icon?: React.ReactNode
}

const DOC_DEBOUNCE_MS = 350

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: session } = useSession()
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(0)
  const [missions, setMissions] = useState<MissionLite[]>([])
  const [docHits, setDocHits] = useState<SearchHit[]>([])
  const [docLoading, setDocLoading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const groups: string[] = (session?.user as any)?.groups ?? []
  const isAdmin = groups.some((g: string) =>
    ['admin', 'amiral_admins', 'authentik admins'].includes(g.toLowerCase().trim()))

  // Reset + focus + charger les missions à chaque ouverture.
  useEffect(() => {
    if (!open) return
    setQuery('')
    setSelected(0)
    setDocHits([])
    requestAnimationFrame(() => inputRef.current?.focus())
    if (session) {
      fetch('/api/missions?limit=500')
        .then(r => (r.ok ? r.json() : []))
        .then(d => setMissions(Array.isArray(d) ? d : []))
        .catch(() => {})
    }
  }, [open, session])

  // Connaissances — requête live debounced, annule la précédente en vol.
  useEffect(() => {
    if (!open || !session) return
    const q = query.trim()
    if (q.length < 2) { setDocHits([]); setDocLoading(false); return }

    const t = setTimeout(() => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setDocLoading(true)
      fetch(`/api/search?q=${encodeURIComponent(q)}&sources=es,memory,legal&limit=5`, { signal: controller.signal })
        .then(r => (r.ok ? r.json() : null))
        .then(d => setDocHits(Array.isArray(d?.hits) ? d.hits : []))
        .catch(() => {})
        .finally(() => setDocLoading(false))
    }, DOC_DEBOUNCE_MS)

    return () => clearTimeout(t)
  }, [query, open, session])

  const disabledModules = useModulePrefs(s => s.disabled)

  const results = useMemo<Result[]>(() => {
    const q = query.trim().toLowerCase()

    const navItems: NavItemType[] = NAV_SECTIONS
      .filter(s => !s.roles || isAdmin)
      .flatMap(s => s.items)
      .filter(it => isHrefVisible(disabledModules, it.href))
    const navResults: Result[] = (q ? navItems.filter(it => it.label.toLowerCase().includes(q)) : navItems)
      .slice(0, 8)
      .map(it => ({ kind: 'nav', key: `nav-${it.href}`, label: it.label, href: it.href, icon: it.icon }))

    const missionResults: Result[] = q.length === 0 ? [] : missions
      .filter(m => m.objective.toLowerCase().includes(q))
      .slice(0, 6)
      .map(m => ({ kind: 'mission', key: `mission-${m.id}`, label: m.objective, detail: m.status, href: `/missions/${m.id}` }))

    const docResults: Result[] = docHits.slice(0, 5).map((h, i) => ({
      kind: 'doc', key: `doc-${i}`, label: h.title || h.url || 'Résultat', detail: h.source, href: h.url || '/knowledge',
    }))

    return [...navResults, ...missionResults, ...docResults]
  }, [query, missions, docHits, isAdmin, disabledModules])

  const go = useCallback((r: Result) => {
    onClose()
    router.push(r.href)
  }, [onClose, router])

  // Raccourci global Ctrl+K / Cmd+K — ouverture ; géré par le parent (Topbar).
  // Ici : navigation clavier + fermeture une fois ouvert.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, results.length - 1)) }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)) }
      if (e.key === 'Enter') { e.preventDefault(); const r = results[selected]; if (r) go(r) }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, results, selected, onClose, go])

  if (!open) return null

  const KIND_LABEL: Record<ResultKind, string> = { nav: 'NAVIGATION', mission: 'MISSIONS', doc: 'CONNAISSANCES' }
  let runningIndex = -1

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 92vw)', maxHeight: '60vh', display: 'flex', flexDirection: 'column',
          background: 'rgba(6,8,16,0.98)', border: '1px solid rgba(79,124,255,0.2)', borderRadius: 14,
          boxShadow: '0 20px 80px rgba(0,0,0,0.6)', overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={{ opacity: 0.4 }}>⌘</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(0) }}
            placeholder="Rechercher une mission, un document, une destination…"
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontSize: 13 }}
          />
          <span style={{ fontSize: 9, opacity: 0.3, fontFamily: 'var(--font-mono)' }}>ESC</span>
        </div>

        <div style={{ overflowY: 'auto', padding: '6px 0' }}>
          {(['nav', 'mission', 'doc'] as ResultKind[]).map(kind => {
            const items = results.filter(r => r.kind === kind)
            if (items.length === 0) return null
            return (
              <div key={kind}>
                <div style={{ padding: '6px 16px 2px', fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.25)', fontFamily: 'var(--font-mono)' }}>
                  {KIND_LABEL[kind]}
                </div>
                {items.map(r => {
                  runningIndex += 1
                  const idx = runningIndex
                  const active = idx === selected
                  return (
                    <div
                      key={r.key}
                      onMouseEnter={() => setSelected(idx)}
                      onClick={() => go(r)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px', cursor: 'pointer',
                        background: active ? 'rgba(79,124,255,0.1)' : 'transparent',
                        borderLeft: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
                      }}
                    >
                      <span style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 12, color: active ? 'var(--text-primary)' : 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.label}
                      </span>
                      {r.detail && (
                        <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{r.detail}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}

          {docLoading && (
            <div style={{ padding: '8px 16px', fontSize: 10, color: 'rgba(255,255,255,0.2)', fontFamily: 'var(--font-mono)' }}>⟳ Recherche…</div>
          )}
          {!docLoading && results.length === 0 && (
            <div style={{ padding: '16px', fontSize: 11, color: 'rgba(255,255,255,0.25)', fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
              {query.trim() ? 'Aucun résultat' : 'Tapez pour rechercher…'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
